import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { Col, Form, InputNumber, Row, Select, Spin, Alert, Radio } from "antd"
import { LoadingOutlined } from "@ant-design/icons"

// Components
import Button from "src/components/MyButton/Button"
import CustomModal from "src/components/Modal/CustomModal"
import CRMService from "src/services/CRMService"
import DepartmentService from "src/services/DepartmentService"
import Notice from "src/components/Notice"
import { SelectStyle } from "../common/styled"

const ModalFastAssigner = props => {
  const { open, onCancel, onSuccess, filter, type, searchTerm, total } = props

  const [form] = Form.useForm()
  const [assignMode, setAssignMode] = useState("assign") // 'assign' (Chia số cho Sale) | 'recall' (Thu hồi số)
  const [listDepartment, setListDepartment] = useState([])
  const [departmentId, setDepartmentId] = useState(null)
  const [assignerList, setAssignerList] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const personnelCache = useRef({})

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        const departmentRes = await DepartmentService.getAllDept()
        setListDepartment(departmentRes.success || [])
      } catch (err) {
        console.error("Error fetching department:", err)
      }
    }
    if (open) {
      setAssignMode("assign")
      fetchDepartment()
    }
  }, [open])

  // Fetch personnel list by departmentId
  const fetchPersonnelList = async (deptId, mode = assignMode) => {
    if (!deptId) {
      setAssignerList([])
      return
    }

    const applySelection = (list) => {
      if (mode === "recall" && list.length > 0) {
        const adminPerson = list.find(
          p => p.label?.toLowerCase() === "admin" || p.value === 33 || p.position_id === 1
        )
        const selectedId = adminPerson ? adminPerson.value : list[0].value
        form.setFieldsValue({ personnel_ids: [selectedId] })
      }
    }

    if (personnelCache.current[deptId]) {
      const cached = personnelCache.current[deptId]
      setAssignerList(cached)
      applySelection(cached)
      return
    }

    setLoading(true)
    try {
      const response = await CRMService.getListPersonnel({
        department_id: deptId,
        positions: [1, 2, 3, 4],
      })

      if (response && response.data) {
        const formattedData = response.data
          .filter(person => person?.user?.is_locked === 0)
          .map(item => ({
            value: item.ID,
            label: item.name,
            position_id: item.position_id,
          }))

        personnelCache.current[deptId] = formattedData
        setAssignerList(formattedData)
        applySelection(formattedData)
      } else {
        setAssignerList([])
      }
    } catch (error) {
      console.error("fetchPersonnelList error", error)
      setAssignerList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (departmentId) {
      fetchPersonnelList(departmentId, assignMode)
    }
  }, [departmentId, assignMode])

  const handleModeChange = mode => {
    setAssignMode(mode)
    if (mode === "recall") {
      form.setFieldsValue({
        department_id: 2,
        personnel_ids: [],
      })
      setDepartmentId(2)
    } else {
      form.resetFields()
      setDepartmentId(null)
      form.setFieldsValue({
        quantity_per_sale: 1,
      })
    }
  }

  const handleDepartmentChange = val => {
    setDepartmentId(val)
    form.setFieldsValue({ personnel_ids: [] })
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)

      const payload = {
        type: type !== "all" && type !== "renewBefore3Months" && type !== "newArchive" ? type : undefined,
        keyword: searchTerm || undefined,
        filter: filter || {},
        department_id: values.department_id,
        personnel_ids: values.personnel_ids,
        quantity_per_sale: values.quantity_per_sale,
      }

      const response = await CRMService.postFastAssigned(payload)

      if (response?.error || response?.isError) {
        Notice({
          isSuccess: false,
          msg: response?.message || (assignMode === "recall" ? "Thu hồi số thất bại!" : "Chia số nhanh thất bại!"),
        })
      } else {
        const isQueued = response?.queued || response?.data?.queued
        Notice({
          isSuccess: true,
          msg: response?.message || response?.data?.message || (isQueued ? "Yêu cầu đã được đưa vào hàng chờ xử lý ngầm!" : assignMode === "recall" ? "Thu hồi số thành công!" : "Chia số nhanh thành công!"),
        })
        form.resetFields()
        setDepartmentId(null)
        setAssignMode("assign")
        onCancel()
        if (onSuccess) onSuccess()
      }
    } catch (error) {
      console.error("handleSave fastAssigned error:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const selectedPersonnels = Form.useWatch("personnel_ids", form) || []
  const quantityPerSale = Form.useWatch("quantity_per_sale", form) || 0
  const totalAssignedCalculate = selectedPersonnels.length * quantityPerSale
  const isExceed = totalAssignedCalculate > (total || 0)

  return (
    <CustomModal
      title={assignMode === "recall" ? "Thu hồi số nhanh" : "Chia số nhanh cho Sale"}
      size="small"
      open={open}
      onCancel={() => {
        form.resetFields()
        setDepartmentId(null)
        setAssignMode("assign")
        onCancel()
      }}
      footer={
        <Row justify="end" gutter={12}>
          <Col>
            <Button
              style={{
                borderRadius: 6,
                backgroundColor: "#f5f5f5",
                color: "#595959",
                borderColor: "#d9d9d9",
                fontWeight: 500,
              }}
              onClick={() => {
                form.resetFields()
                setDepartmentId(null)
                setAssignMode("assign")
                onCancel()
              }}
            >
              Hủy
            </Button>
          </Col>
          <Col>
            <Button
              btnType={assignMode === "recall" ? "danger" : "primary"}
              loading={submitting}
              disabled={submitting || selectedPersonnels.length === 0 || quantityPerSale <= 0 || isExceed}
              style={{
                borderRadius: 6,
                backgroundColor:
                  submitting || selectedPersonnels.length === 0 || quantityPerSale <= 0 || isExceed
                    ? undefined
                    : assignMode === "recall"
                    ? "#ff4d4f"
                    : "#1890ff",
                color: "#fff",
                fontWeight: 600,
                boxShadow:
                  submitting || selectedPersonnels.length === 0 || quantityPerSale <= 0 || isExceed
                    ? "none"
                    : assignMode === "recall"
                    ? "0 2px 6px rgba(255, 77, 79, 0.35)"
                    : "0 2px 6px rgba(24, 144, 255, 0.35)",
              }}
              onClick={handleSave}
            >
              {assignMode === "recall" ? "Xác nhận thu hồi" : "Xác nhận chia số"}
            </Button>
          </Col>
        </Row>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          quantity_per_sale: 1,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              <div>
                <strong>Tổng số liên hệ thỏa mãn lọc: </strong>
                <span style={{ color: "#1890ff", fontSize: 16, fontWeight: "bold" }}>
                  {total || 0}
                </span>{" "}
                liên hệ
              </div>
            }
            type="info"
            showIcon
          />
        </div>

        <Form.Item label="Tùy chọn thao tác" style={{ marginBottom: 16 }}>
          <Radio.Group
            value={assignMode}
            onChange={e => handleModeChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            style={{ width: "100%", display: "flex" }}
          >
            <Radio.Button value="assign" style={{ flex: 1, textAlign: "center" }}>
              Chia số cho Sale
            </Radio.Button>
            <Radio.Button value="recall" style={{ flex: 1, textAlign: "center" }}>
              Thu hồi số
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={assignMode === "recall" ? "Phòng ban thu hồi" : "Phòng ban cần chia"}
          name="department_id"
          rules={[{ required: true, message: "Vui lòng chọn phòng ban!" }]}
        >
          <SelectStyle
            placeholder="Chọn phòng ban"
            disabled={assignMode === "recall"}
            onChange={handleDepartmentChange}
            options={
              assignMode === "recall"
                ? listDepartment
                    .filter(item => item.ID === 2 || item.ID === "2")
                    .map(item => ({
                      value: item.ID,
                      label: item.title || item.department_name || item.name,
                    }))
                : listDepartment
                    .filter(item => item.ID !== 2 && item.ID !== "2")
                    .map(item => ({
                      value: item.ID,
                      label: item.title || item.department_name || item.name,
                    }))
            }
          />
        </Form.Item>

        <Form.Item
          label={assignMode === "recall" ? "Người nhận thu hồi (Admin)" : "Danh sách Sale trong phòng"}
          name="personnel_ids"
          rules={[{ required: true, message: assignMode === "recall" ? "Vui lòng chọn người nhận thu hồi!" : "Vui lòng chọn ít nhất 1 sale!" }]}
        >
          <SelectStyle
            mode="multiple"
            placeholder={departmentId ? (assignMode === "recall" ? "Chọn người nhận thu hồi" : "Chọn danh sách Sale") : "Vui lòng chọn phòng ban trước"}
            disabled={!departmentId || loading}
            options={assignerList}
            notFoundContent={loading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} /> : null}
          />
        </Form.Item>

        <Form.Item
          label={assignMode === "recall" ? "Số lượng liên hệ thu hồi" : "Số lượng liên hệ gán cho 1 Sale"}
          name="quantity_per_sale"
          rules={[
            { required: true, message: "Vui lòng nhập số lượng!" },
            { type: "number", min: 1, message: "Số lượng phải lớn hơn 0!" },
          ]}
        >
          <InputNumber
            min={1}
            style={{ width: "100%", borderRadius: 6 }}
            placeholder="Nhập số lượng (Ví dụ: 10)"
          />
        </Form.Item>

        {selectedPersonnels.length > 0 && quantityPerSale > 0 && (
          isExceed ? (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 6,
              }}
            >
              <span style={{ color: "#ff4d4f", fontWeight: 500 }}>
                ⚠️ <strong>Cảnh báo:</strong> Dự kiến {assignMode === "recall" ? "thu hồi" : "chia"} <strong>{totalAssignedCalculate}</strong> liên hệ vượt quá tổng số liên hệ khả dụng hiện tại (<strong>{total || 0}</strong> liên hệ). Vui lòng giảm số lượng!
              </span>
            </div>
          ) : assignMode === "assign" ? (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 6,
              }}
            >
              <span style={{ color: "#389e0d", fontWeight: 500 }}>
                Dự kiến chia tổng cộng <strong>{totalAssignedCalculate}</strong> số liên hệ
                (Mỗi sale nhận <strong>{quantityPerSale}</strong> số, chọn <strong>{selectedPersonnels.length}</strong> sale).
              </span>
            </div>
          ) : null
        )}
      </Form>
    </CustomModal>
  )
}

ModalFastAssigner.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  filter: PropTypes.object,
  type: PropTypes.string,
  searchTerm: PropTypes.string,
  total: PropTypes.number,
}

export default ModalFastAssigner
